"use client";

import { useState } from "react";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface VoteButtonsProps {
    inbId: string;
    initialUpvotes: number;
    initialDownvotes: number;
    initialUserVote?: number | null;
}

export default function VoteButtons({
    inbId,
    initialUpvotes,
    initialDownvotes,
    initialUserVote = null,
}: VoteButtonsProps) {
    const [upvotes, setUpvotes] = useState(initialUpvotes);
    const [downvotes, setDownvotes] = useState(initialDownvotes);
    const [userVote, setUserVote] = useState<number | null>(initialUserVote);

    const handleVote = async (value: number) => {
        // Optimistic update - don't block if loading

        const previousVote = userVote;
        const previousUpvotes = upvotes;
        const previousDownvotes = downvotes;

        // Calculate new state locally
        let newVote: number | null = value;
        let newUp = upvotes;
        let newDown = downvotes;

        if (userVote === value) {
            // Remove vote
            newVote = null;
            if (value === 1) newUp--;
            else newDown--;
        } else {
            // Change or add vote
            if (userVote === 1) newUp--;
            else if (userVote === -1) newDown--;

            newVote = value;
            if (value === 1) newUp++;
            else newDown++;
        }

        // Apply optimistic state
        setUserVote(newVote);
        setUpvotes(newUp);
        setDownvotes(newDown);

        try {
            const response = await fetch("/api/votes", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ inbId, value: userVote === value ? 0 : value }),
            });

            if (!response.ok) {
                // Revert on error
                setUserVote(previousVote);
                setUpvotes(previousUpvotes);
                setDownvotes(previousDownvotes);
            }
        } catch {
            // Revert on error
            setUserVote(previousVote);
            setUpvotes(previousUpvotes);
            setDownvotes(previousDownvotes);
        }
    };

    const score = upvotes - downvotes;

    return (
        <div className="flex items-center gap-3">
            <button
                onClick={() => handleVote(1)}
                className={cn(
                    "vote-btn vote-btn-up",
                    userVote === 1 && "active"
                )}
                aria-label="Zagłosuj za"
            >
                <ThumbsUp size={20} />
            </button>

            <div className="flex flex-col items-center min-w-[60px]">
                <span
                    className={cn(
                        "text-lg font-bold",
                        score > 0 && "text-green-500",
                        score < 0 && "text-red-500",
                        score === 0 && "text-gray-400"
                    )}
                >
                    {score > 0 ? `+${score}` : score}
                </span>
                <span className="text-xs text-gray-500">
                    {upvotes}↑ {downvotes}↓
                </span>
            </div>

            <button
                onClick={() => handleVote(-1)}
                className={cn(
                    "vote-btn vote-btn-down",
                    userVote === -1 && "active"
                )}
                aria-label="Zagłosuj przeciw"
            >
                <ThumbsDown size={20} />
            </button>
        </div>
    );
}
